<template>
  <div class="pdp-product-image-panel">
    <pdp-product-image-zoom
      v-if="isZoomActive"
      :base-properties="baseProperties"
      :images="items"
      :current-image-index="selectedItemIndex"
      :product-type="productType"
      @close="closeZoom"
    />
    <div id="heroCarousel" :class="{'has-accessories-strip': hasAccessoriesStrip}">
      <swiper ref="slider" :options="swiperOptions">
        <swiper-slide v-for="(item, i) in items" :key="`${item.data.assetId}-${i}`">
          <template v-if="item.type !== 'video'">
            <img
              :data-srcset="i > 0 ? getSrcSet(item.data.assetId) : ''"
              :srcset="!i ? getSrcSet(item.data.assetId) : ''"
              class="swiper-lazy"
              @click="slideClicked"
            />
            <div v-if="i > 0" class="swiper-lazy-preloader swiper-lazy-preloader-white"></div>
            <div v-if="item.data.isAccessorized" class="accessories-disclaimer">
              <span v-if="productType === 'bike'" v-html="bp('accessorizedCaption')" />
              <span v-else v-html="bp('accessorizedAftermarketCaption')" />
            </div>
          </template>
        </swiper-slide>
        <div slot="button-prev" class="swiper-button-prev hidden md:flex" />
        <div slot="button-next" class="swiper-button-next hidden md:flex" />
        <div v-show="items.length > 1" slot="pagination" class="swiper-pagination lg:hidden"></div>
      </swiper>
    </div>
    <div v-if="items.length > 1" id="heroCarousel--thumbnails">
      <div
        v-for="(item, index) in items"
        :key="`${item.data.assetId}-${index}`"
        class="small-2 thumbnail-item"
        :class="{synched: selectedItemIndex === index}"
        @click.prevent="switchTo(index)"
      >
        <div v-if="item.type === 'image'" class="img-wrapper">
          <div class="img-wrapper-inner">
            <img :src="getThumbnailUrl(item.data.assetId)" />
          </div>
        </div>
        <div v-else-if="item.type === 'video'" class="vid-wrapper">
          <a
            :id="`video-${item.data.id}`"
            :href="item.data.url"
            class="pdp-thumbnails__video-link trek_video play-icon-after gallery-vid"
            :data-video="item.data.id"
            :data-ga-action="item.data.caption"
            data-ga-event="video-engagement"
            data-ga-category="Video Engagement"
            data-ga-label="Play"
          >
            <img :src="getThumbnailUrl(item.data.assetId || item.data.scene7StillAssetId, {fit: 'crop'})" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import get from 'lodash/get';
import {ProductImages} from '@/components/containers/pdp/utils/product-images';
import {swiper, swiperSlide} from 'vue-awesome-swiper';
import 'swiper/dist/css/swiper.css';
import PdpProductImageZoom from '@/components/containers/pdp/PdpProductImageZoom';

export default {
  name: 'PdpProductImagePanel',
  components: {
    PdpProductImageZoom,
    swiper,
    swiperSlide,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    maxThumbnails: {
      type: Number,
      default: 12,
    },
    color: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedItemIndex: 0,
      swiperOptions: {
        preloadImages: true,
        lazy: true,
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        on: {
          slideChange: () => {
            this.selectedItemIndex = this.swiper.activeIndex;
          },
        },
      },
      isZoomActive: false,
    };
  },
  computed: {
    hasAccessoriesStrip() {
      if (!this.items) {
        return false;
      }
      return this.items.filter((item) => item.data.isAccessorized).length > 0;
    },
    selectedVariantCode() {
      return this.$store.state.savedLists.buyingZoneEntries.length
        ? this.$store.state.savedLists.buyingZoneEntries[0].sku
        : undefined;
    },
    productType() {
      return this.product.productType;
    },
    items() {
      if (!this.product.images || !Object.keys(this.product.images).length) {
        return [];
      }

      const result = [];
      const images = this.getImagesForCurrentVariant();
      const videos = get(this.product, `videos`);

      if (images && images.length > 0) {
        images.sort((a, b) => {
          return a.weight > b.weight ? 1 : a.weight < b.weight ? -1 : 0;
        });
      }

      const push = (item, type) => {
        if (result.length >= this.maxThumbnails) {
          return false;
        }

        result.push({type, data: item});
        return true;
      };

      if (images && images.length > 0) {
        for (let item of images) {
          if (!push(item, 'image')) {
            break;
          }
        }
      }

      if (videos && videos.length > 0 && result.length < this.maxThumbnails) {
        for (let item of videos) {
          if (!push(item, 'video')) {
            break;
          }
        }
      }

      return result;
    },
    swiper() {
      return this.$refs.slider.swiper;
    },
  },
  watch: {
    color(value, oldValue) {
      if (value !== oldValue) {
        this.switchTo(0);
      }
    },

    selectedVariantCode(value, oldValue) {
      if (value !== oldValue) {
        this.switchTo(0);
      }
    },

    isZoomActive(isActive) {
      if (isActive) {
        this.stopListeningKeyboard();
      } else {
        this.startListeningKeyboard();
      }
    },
  },
  mounted() {
    this.startListeningKeyboard();
  },
  beforeDestroy() {
    this.stopListeningKeyboard();
  },
  methods: {
    getImagesForCurrentVariant() {
      const color = this.color || Object.keys(this.product.images)[0];
      let images = this.selectedVariantCode
        ? this.product.images[color].filter((item) => item.variantList.includes(this.selectedVariantCode))
        : this.product.images[color];

      if (!images.length) {
        if (!this.color) {
          Object.keys(this.product.images).forEach((groupKey) => {
            const group = this.product.images[groupKey];
            group.forEach((item) => {
              if (item.variantList.includes(this.selectedVariantCode)) {
                images.push(item);
              }
            });
          });
        } else {
          images = this.product.images[color];
        }
      }

      return this.filterVariantImages(images);
    },
    filterVariantImages(images) {
      const variantImages = [];

      if (!images || !images.length) {
        return images;
      }

      if (this.product.variants && this.product.variants.length > 0) {
        this.product.variants.forEach((variant) => {
          if (this.product.isArchived === variant.isArchived) {
            variantImages.push(variant.code);
          }
        });
      }

      return images.filter(
        (image) => image.variantList && image.variantList.some((item) => variantImages.includes(item))
      );
    },
    slideClicked() {
      this.isZoomActive = true;
    },
    closeZoom() {
      this.isZoomActive = false;
    },
    startListeningKeyboard() {
      this.stopListeningKeyboard();
      document.addEventListener('keydown', this.keyPressed);
    },
    stopListeningKeyboard() {
      document.removeEventListener('keydown', this.keyPressed);
    },
    keyPressed(ev) {
      if (ev.keyCode === 37) {
        this.goToPreviousItem();
      } else if (ev.keyCode === 39) {
        this.goToNextItem();
      }
    },
    getThumbnailUrl: (assetId, options = {}) => ProductImages.getThumbnailUrl(assetId, options),
    getSrcSet: (assetId, options = {}) => ProductImages.getSrcSet(assetId, options),
    triggerVideoItem() {
      this.$nextTick(() => {
        if (this.items[this.selectedItemIndex].type === 'video') {
          const videoEl = this.$el.querySelector('.synched .pdp-thumbnails__video-link');
          if (videoEl) {
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });

            videoEl.dispatchEvent(clickEvent);
          }
        }
      });
    },
    goToPreviousItem() {
      let newItemIndex = this.selectedItemIndex - 1;
      if (newItemIndex < 0) {
        newItemIndex = this.items.length - 1;
      }

      this.switchTo(newItemIndex);
      this.triggerVideoItem();
    },
    goToNextItem() {
      this.switchTo(this.selectedItemIndex + 1);
      this.triggerVideoItem();
    },
    switchTo(itemIndex) {
      if (itemIndex < 0 || itemIndex >= this.items.length) {
        itemIndex = 0;
      }

      this.selectedItemIndex = itemIndex;
      this.swiper.slideTo(itemIndex);
    },
  },
};
</script>
<style lang="scss">
.pdp-product-image-panel {
  .swiper-button-prev,
  .swiper-button-next {
    align-items: center;
    justify-content: center;
    background-image: none;

    &.swiper-button-disabled {
      display: none;
    }

    &,
    &:after {
      width: auto;
    }

    &:after {
      font-family: 'Material Icons';
      line-height: 1;
      font-size: 3rem;
      color: var(--gray-20);
    }

    &:hover:after {
      color: var(--gray-100);
    }
  }

  .swiper-button-next {
    right: 0;

    &:after {
      content: 'chevron_right';
    }
  }

  .swiper-container-horizontal > .swiper-pagination-bullets .swiper-pagination-bullet {
    background-color: var(--gray-20);
    opacity: 1;
    padding: 0;
    font-size: 0;
    margin-right: 0.5rem;
    border-radius: 100%;

    &.swiper-pagination-bullet-active {
      background-color: var(--gray-100);
    }
  }

  .pdp-product-image-zoom .swiper-container-horizontal > .swiper-pagination-bullets .swiper-pagination-bullet {
    border-radius: 1px;
    margin-right: 1rem;
    width: 1.5rem;
    height: 0.1875rem;
  }

  .swiper-button-prev {
    left: 0;

    &:after {
      content: 'chevron_left';
    }
  }
}
</style>
